import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  CalendarDays, Sparkles, PlusCircle, Palmtree, HeartPulse, Clock, PartyPopper,
  Users, X, Zap, Baby, Printer, Pencil, Trash2, MinusCircle, ArrowLeftRight, ClipboardList, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MONTHS_IT, WEEKDAYS_SHORT, fmtHours, CAUSE_LABELS, CAUSE_COLORS, lastCompletedMonth, hydrateCauseCatalog } from "@/lib/constants";
import { parseISOLocal } from "@/lib/dates";
import { SuiteCard, SuiteButton, SuiteEmployeePicker, SuiteEyebrow, SuiteKpi, SuiteBadge, SuiteMonthPicker } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { downloadBlob } from "@/lib/download";
import { useEffectiveRole } from "@/foundation/context";
import { DurationInput, DurationLabel } from "@/foundation/duration";

const EXCEPTION_TYPES = [
  { code: "FERIE", label: "Ferie", icon: Palmtree },
  { code: "MALATTIA", label: "Malattia", icon: HeartPulse },
  { code: "PERMESSI", label: "Permessi", icon: Clock },
  { code: "PERMESSI_104", label: "L.104", icon: Clock },
  { code: "MATERNITA", label: "Maternità", icon: Baby },
  { code: "CONG_PARENTALE", label: "Cong. Parentale", icon: Clock },
  { code: "ALLATTAMENTO", label: "Allattamento", icon: Baby },
  { code: "RECUPERO", label: "Recupero", icon: Zap },
  { code: "STRAORDINARIO", label: "Straordinario", icon: PlusCircle },
  { code: "ASSENZA_NR", label: "Assenza n.r.", icon: MinusCircle },
];

/** Data-default "smart" per il modal aperto senza contesto:
 *  - se oggi cade nel mese/anno attivo → ritorna oggi
 *  - altrimenti → primo giorno lavorativo (lun-ven) del mese attivo
 */
function smartDefaultDate(year, month) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  if (y === year && m === month) {
    const dd = String(today.getDate()).padStart(2, "0");
    return `${year}-${String(month).padStart(2, "0")}-${dd}`;
  }
  // Primo giorno lavorativo del mese attivo (lun-ven)
  for (let day = 1; day <= 7; day++) {
    const d = new Date(year, month - 1, day);
    const dow = d.getDay(); // 0=dom, 6=sab
    if (dow !== 0 && dow !== 6) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

function causeRangeDayMode(cause) {
  if (cause?.behavior?.range_day_mode) return cause.behavior.range_day_mode;
  if (cause?.code === "CONGEDO_MATRIMONIALE") return "calendar_days";
  if (["MALATTIA", "MATERNITA", "CONG_PARENTALE"].includes(cause?.code)) return "exclude_sundays";
  return "working_days";
}

export default function ModalitaGuidata() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const _dm = lastCompletedMonth();
  const [year, setYear] = useState(_dm.year);
  const [month, setMonth] = useState(_dm.month);
  const [summary, setSummary] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [causes, setCauses] = useState([]);
  const [initLoading, setInitLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    employee_id: "", date: "", end_date: "", cause: "FERIE", hours: 8,
    use_profile_hours: true, range_day_mode: "working_days", notes: "",
    original_date: null,
    theoretical_hours: null,
    allattamento_max_effettivo: null,
    allattamento_max_normativa: null,
    worked_hours: null,
  });

  // Load companies for admin
  useEffect(() => {
    if (!["admin", "super_admin"].includes(effectiveRole)) return;
    api.get("/companies")
      .then(({ data }) => {
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      })
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  const load = async () => {
    if (!companyId) return;
    try {
      const [s, h, c] = await Promise.all([
        api.get("/guided/exceptions-summary", { params: { company_id: companyId, year, month } }),
        api.get("/guided/holidays", { params: { year, company_id: companyId } }),
        api.get("/causes"),
      ]);
      setSummary(s.data);
      setHolidays(h.data.holidays.filter((x) => x.date.startsWith(`${year}-${String(month).padStart(2, "0")}`)));
      setCauses(hydrateCauseCatalog(c.data));
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [companyId, year, month]);

  const initMonth = async () => {
    if (!companyId) { toast.error("Seleziona un'azienda"); return; }
    if (!window.confirm(`Preparare ${MONTHS_IT[month - 1]} ${year} inserendo automaticamente festività nazionali e Santo Patrono?`)) return;
    setInitLoading(true);
    try {
      const { data } = await api.post("/guided/init-month", {
        company_id: companyId, year, month,
        auto_holidays: true, auto_santo_patrono: true, only_missing: true,
      });
      toast.success(`Inserite ${data.holidays_inserted} festività (${data.holidays_skipped} già presenti saltate)`);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setInitLoading(false); }
  };

  const fetchDayContext = async (employee_id, date) => {
    if (!employee_id || !date) return null;
    try {
      const { data } = await api.get("/guided/day-context", { params: { employee_id, date } });
      return data;
    } catch (_) { return null; }
  };

  const openException = async (empId, date, existingCause = null) => {
    const emp = summary?.employees.find((e) => e.employee_id === empId);
    const existing = existingCause
      ? emp?.exceptions.find((x) => x.date === date && x.cause === existingCause)
      : emp?.exceptions.find((x) => x.date === date);
    // Data smart di default se non specificata
    const targetDate = date || smartDefaultDate(year, month);
    const ctx = await fetchDayContext(empId, targetDate);
    const selectedCause = causes.find((row) => row.code === (existing?.cause || "FERIE"));
    setModalData({
      employee_id: empId,
      date: targetDate,
      end_date: targetDate,
      cause: existing?.cause || "FERIE",
      hours: existing?.hours ?? (ctx?.theoretical_hours || 8),
      use_profile_hours: !existing,
      range_day_mode: causeRangeDayMode(selectedCause),
      notes: existing?.notes || "",
      original_date: existing ? existing.date : null,
      original_cause: existing ? existing.cause : null,
      theoretical_hours: ctx?.theoretical_hours ?? null,
      worked_hours: ctx?.worked_hours ?? null,
      allattamento_max_effettivo: ctx?.allattamento_max_effettivo ?? null,
      allattamento_max_normativa: ctx?.allattamento_max_normativa ?? null,
    });
    setModalOpen(true);
  };

  const handleCauseChange = async (code) => {
    const theo = modalData.theoretical_hours;
    let suggested = modalData.hours;
    if (code === "ALLATTAMENTO") {
      const ctx = await fetchDayContext(modalData.employee_id, modalData.date);
      const maxEff = ctx?.allattamento_max_effettivo ?? 1;
      suggested = maxEff;
      setModalData({
        ...modalData, cause: code, hours: suggested,
        theoretical_hours: ctx?.theoretical_hours ?? theo,
        worked_hours: ctx?.worked_hours ?? null,
        allattamento_max_effettivo: maxEff,
        allattamento_max_normativa: ctx?.allattamento_max_normativa ?? 1,
      });
      return;
    }
    if (["FERIE", "MALATTIA", "PERMESSI_104", "FESTIVITA", "SANTO_PATRONO", "CONG_PARENTALE", "MATERNITA"].includes(code)) {
      suggested = theo && theo > 0 ? theo : 8;
    } else if (["PERMESSI", "RECUPERO", "STRAORDINARIO"].includes(code)) {
      suggested = 2;
    }
    const selectedCause = causes.find((row) => row.code === code);
    setModalData({
      ...modalData,
      cause: code,
      hours: suggested,
      range_day_mode: causeRangeDayMode(selectedCause),
    });
  };

  const saveException = async () => {
    if (!modalData.employee_id) { toast.error("Seleziona dipendente"); return; }
    if (!modalData.date) { toast.error("Seleziona data"); return; }
    if (!modalData.end_date) { toast.error("Seleziona la data finale"); return; }
    if (modalData.end_date < modalData.date) { toast.error("La data finale deve essere successiva o uguale alla data iniziale"); return; }
    if (!modalData.use_profile_hours && Number(modalData.hours) <= 0) { toast.error("Ore > 0"); return; }
    if (modalData.cause === "ALLATTAMENTO" && modalData.allattamento_max_effettivo !== null) {
      if (Number(modalData.hours) > modalData.allattamento_max_effettivo + 0.001) {
        toast.error(`Massimo consentito per allattamento: ${fmtHours(modalData.allattamento_max_effettivo)}h (normativa ${modalData.allattamento_max_normativa}h, cappato dalla differenza col profilo)`);
        return;
      }
    }
    try {
      if (modalData.original_date && modalData.original_date !== modalData.date) {
        await api.delete("/guided/exception", {
          params: { employee_id: modalData.employee_id, date: modalData.original_date }
        });
      }
      if (!modalData.original_date && (modalData.end_date !== modalData.date || modalData.use_profile_hours)) {
        const { data } = await api.post("/guided/exception-range", {
          employee_id: modalData.employee_id,
          start_date: modalData.date,
          end_date: modalData.end_date,
          cause: modalData.cause,
          hours: modalData.use_profile_hours ? null : Number(modalData.hours),
          use_profile_hours: modalData.use_profile_hours,
          range_day_mode: modalData.range_day_mode,
          notes: modalData.notes || null,
          replace_existing: true,
        });
        toast.success(
          `Variazione applicata: ${data.created + data.updated} giornate`
          + (data.calendar_days ? ` · ${data.calendar_days} giorni di calendario` : "")
          + (data.skipped ? ` · ${data.skipped} giorni saltati` : "")
        );
      } else {
        await api.post("/guided/exception", {
          employee_id: modalData.employee_id,
          date: modalData.date,
          cause: modalData.cause,
          hours: Number(modalData.hours),
          notes: modalData.notes || null,
          replace_existing: true,
        });
        toast.success(modalData.original_date ? "Variazione aggiornata" : "Variazione registrata");
      }
      setModalOpen(false);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeExceptionFromModal = async () => {
    if (!modalData.original_date) { setModalOpen(false); return; }
    if (!window.confirm(`Rimuovere la variazione del ${modalData.original_date}?`)) return;
    try {
      await api.delete("/guided/exception", {
        params: { employee_id: modalData.employee_id, date: modalData.original_date }
      });
      toast.success("Variazione rimossa");
      setModalOpen(false);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async () => {
    if (!companyId) { toast.error("Seleziona un'azienda"); return; }
    setPdfLoading(true);
    try {
      const res = await api.get("/guided/exceptions-pdf", {
        params: { company_id: companyId, year, month },
        responseType: "blob",
      });
      downloadBlob(res.data, `Variazioni_${year}-${String(month).padStart(2, "0")}.pdf`, "application/pdf");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setPdfLoading(false); }
  };

  const monthLabel = `${MONTHS_IT[month - 1]} ${year}`;
  const exceptionTypes = useMemo(() => {
    const iconByCode = Object.fromEntries(EXCEPTION_TYPES.map((item) => [item.code, item.icon]));
    const hidden = new Set(["ORDINARIO", "FESTIVITA", "SANTO_PATRONO"]);
    return causes
      .filter((cause) => !hidden.has(cause.code))
      .map((cause) => ({
        code: cause.code,
        label: cause.label || CAUSE_LABELS[cause.code] || cause.code,
        icon: iconByCode[cause.code] || CalendarDays,
      }));
  }, [causes]);

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="assenze-permessi-page">
      {/* HEADER — compatto stile Reference Page: eyebrow + H1 text-2xl + mese cliccabile */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <SuiteEyebrow icon={Sparkles} className="!mb-0">Modulo Presenze</SuiteEyebrow>
          <h1 className="text-2xl font-bold tracking-tight text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
            Assenze e Permessi
          </h1>
          <SuiteMonthPicker
            year={year}
            month={month}
            testid="guided-month-picker"
            onChange={({ year: nextYear, month: nextMonth }) => {
              setYear(nextYear);
              setMonth(nextMonth);
            }}
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <SuiteButton
            as={Link}
            to="/presenze"
            variant="ghost"
            size="sm"
            icon={ArrowLeftRight}
            iconRight={ClipboardList}
            data-testid="switch-to-expert"
          >
            Passa a Gestione Presenze
          </SuiteButton>
          <div className="w-px h-6 bg-suite-neutral-200 mx-1" />
          <SuiteButton
            variant="secondary"
            size="sm"
            icon={Printer}
            onClick={downloadPdf}
            loading={pdfLoading}
            disabled={!companyId || pdfLoading}
            data-testid="guided-print-btn"
          >
            Stampa
          </SuiteButton>
          <SuiteButton
            variant="secondary"
            size="sm"
            icon={Sparkles}
            onClick={initMonth}
            loading={initLoading}
            disabled={!companyId || initLoading}
            data-testid="guided-init-month-btn"
            title="Inserisce automaticamente le festività nazionali e il Santo Patrono per il mese selezionato"
          >
            Prepara il mese
          </SuiteButton>
          <SuiteButton
            size="sm"
            icon={PlusCircle}
            onClick={() => openException("", "")}
            disabled={!companyId}
            data-testid="guided-add-exception-btn"
          >
            Nuova variazione
          </SuiteButton>
        </div>
      </div>

      {/* SOTTOTITOLO conciso */}
      <p className="text-xs text-suite-neutral-500 -mt-2">
        Variazioni non presenti nel cartellino: ferie · permessi · malattia · L.104 · congedi · recuperi.
      </p>

      {/* INFO-STRIP fuso: azienda + festività inline */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-suite-neutral-200 bg-suite-surface px-3 py-2 text-xs"
           data-testid="guided-info-strip">
        {["admin", "super_admin"].includes(effectiveRole) && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Azienda</span>
              <Select value={companyId || ""} onValueChange={setCompanyId}>
                <SelectTrigger className="w-56 h-8 text-xs" data-testid="guided-company-select">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-px h-4 bg-suite-neutral-200" />
          </>
        )}
        {holidays.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap" data-testid="guided-holidays-inline">
            <PartyPopper size={12} className="text-suite-primary" />
            <span className="text-suite-neutral-500 font-semibold">
              {holidays.length} {holidays.length === 1 ? "festività" : "festività"}
            </span>
            {holidays.slice(0, 4).map((h, i) => {
              const dt = parseISOLocal(h.date);
              const dd = dt.getDate();
              const wd = WEEKDAYS_SHORT[(dt.getDay() + 6) % 7];
              const isLocal = h.type === "local";
              const displayName = isLocal ? "Santo Patrono" : h.name;
              return (
                <SuiteBadge key={i} tone="brand" size="sm" className="whitespace-nowrap">
                  <span className="font-mono text-suite-primary/70 mr-1">{wd} {String(dd).padStart(2, "0")}</span>
                  {displayName}
                </SuiteBadge>
              );
            })}
            {holidays.length > 4 && (
              <span className="text-suite-neutral-500 text-[11px]">+{holidays.length - 4}</span>
            )}
          </div>
        ) : (
          <div className="text-suite-neutral-400 text-[11px] flex items-center gap-1.5">
            <PartyPopper size={12} />
            Nessuna festività registrata per {monthLabel}
          </div>
        )}
      </div>

      {/* KPI — 2 gruppi ben distinti */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          {/* Gruppo A — contatori dipendenti (2 card) */}
          <SuiteKpi
            size="sm"
            icon={Users}
            label="Regolari"
            value={summary.n_regular}
            sub="senza variazioni"
            tone="accent"
            testid="stat-regular"
          />
          <SuiteKpi
            size="sm"
            icon={Pencil}
            label="Con variazioni"
            value={summary.n_with_exceptions}
            sub="da verificare"
            tone="warning"
            testid="stat-with-exceptions"
          />
          {/* Gruppo B — ore per causale (4 card) */}
          <SuiteKpi
            size="sm"
            icon={Palmtree}
            label="Ferie"
            value={fmtHours(summary.totals.ferie_h)}
            sub="ore"
            tone="primary"
            testid="stat-ferie"
          />
          <SuiteKpi
            size="sm"
            icon={Clock}
            label="Permessi"
            value={fmtHours(summary.totals.permessi_h)}
            sub="ore"
            tone="warning"
            testid="stat-permessi"
          />
          <SuiteKpi
            size="sm"
            icon={HeartPulse}
            label="Malattia"
            value={fmtHours(summary.totals.malattia_h)}
            sub="ore"
            tone="error"
            testid="stat-malattia"
          />
          <SuiteKpi
            size="sm"
            icon={Zap}
            label="Recupero"
            value={fmtHours(summary.totals.recupero_h)}
            sub="ore"
            tone="secondary"
            testid="stat-recupero"
          />
        </div>
      )}

      {/* LISTA DIPENDENTI */}
      <SuiteCard padding="none" testid="guided-employees-card">
        <div className="px-5 py-3 border-b border-suite-neutral-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-suite-neutral-800 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Users size={16} className="text-suite-primary" />
            Dipendenti <span className="text-suite-neutral-400 font-normal">· {summary?.n_employees || 0}</span>
          </div>
        </div>
        <div className="divide-y divide-suite-neutral-200/70">
          {(summary?.employees || []).map((emp) => {
            const hasExceptions = emp.exceptions.length > 0;
            return (
              <div
                key={emp.employee_id}
                className={`${hasExceptions ? "p-4" : "px-4 py-2.5"} hover:bg-suite-surface-hover transition-colors`}
                data-testid={`guided-emp-${emp.employee_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-suite-neutral-800">{emp.employee_name}</span>
                      <span className="text-[11px] text-suite-neutral-400 font-mono">{emp.matricola || "—"}</span>
                      {hasExceptions ? (
                        <SuiteBadge tone="warn" size="sm">
                          {emp.exceptions.length} {emp.exceptions.length === 1 ? "variazione" : "variazioni"}
                        </SuiteBadge>
                      ) : (
                        <SuiteBadge tone="ok" size="sm">Regolare</SuiteBadge>
                      )}
                    </div>
                    {hasExceptions && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {emp.exceptions.map((e, i) => {
                          const dt = parseISOLocal(e.date);
                          const dd = String(dt.getDate()).padStart(2, "0");
                          const wd = WEEKDAYS_SHORT[(dt.getDay() + 6) % 7];
                          const color = CAUSE_COLORS[e.cause] || CAUSE_COLORS.PERMESSI;
                          return (
                            <button
                              key={i}
                              onClick={() => openException(emp.employee_id, e.date, e.cause)}
                              data-testid={`badge-exc-${emp.employee_id}-${e.date}`}
                              title="Clicca per modificare"
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs transition-colors hover:brightness-95 ${color.bg} ${color.text} ${color.border}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
                              <span className="font-mono text-suite-neutral-500">{wd} {dd}</span>
                              <span className="font-medium">{CAUSE_LABELS[e.cause] || e.cause}</span>
                              <span className="font-mono text-suite-neutral-700">
                                {e.calendar_day ? "calendario" : `${fmtHours(e.hours)}h`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <SuiteButton
                    variant={hasExceptions ? "secondary" : "ghost"}
                    size="sm"
                    icon={PlusCircle}
                    onClick={() => openException(emp.employee_id, "")}
                    data-testid={`guided-add-${emp.employee_id}`}
                  >
                    Aggiungi
                  </SuiteButton>
                </div>
              </div>
            );
          })}
          {(!summary || summary.employees.length === 0) && (
            <div className="p-8 text-center text-suite-neutral-400 text-sm">Nessun dipendente</div>
          )}
        </div>
      </SuiteCard>

      {/* MODAL registrazione/modifica variazione */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent formDialog className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-suite-neutral-800">
              {modalData.original_date ? <Pencil size={18} className="text-suite-primary" /> : <CalendarDays size={18} className="text-suite-primary" />}
              {modalData.original_date ? "Modifica variazione" : "Nuova variazione"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-suite-neutral-600 font-semibold">Dipendente</Label>
              <SuiteEmployeePicker employees={(summary?.employees || []).map((employee) => ({
                id: employee.employee_id, first_name: employee.employee_name, last_name: "",
              }))} value={modalData.employee_id} onValueChange={async (v) => {
                setModalData({ ...modalData, employee_id: v });
                const ctx = await fetchDayContext(v, modalData.date);
                if (ctx) setModalData((prev) => ({ ...prev, employee_id: v,
                  theoretical_hours: ctx.theoretical_hours,
                  worked_hours: ctx.worked_hours,
                  allattamento_max_effettivo: ctx.allattamento_max_effettivo,
                  allattamento_max_normativa: ctx.allattamento_max_normativa,
                }));
              }} className="w-full" testId="modal-emp-select" placeholder="Seleziona dipendente" />
            </div>
            <div>
              <div className={`grid ${modalData.original_date ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
                <div>
                  <Label className="text-xs text-suite-neutral-600 font-semibold">
                    {modalData.original_date ? "Data" : "Dal"}
                  </Label>
                  <Input type="date" value={modalData.date} onChange={async (e) => {
                    const newDate = e.target.value;
                    const previousDate = modalData.date;
                    setModalData({
                      ...modalData,
                      date: newDate,
                      end_date: modalData.end_date === previousDate ? newDate : modalData.end_date,
                    });
                    const ctx = await fetchDayContext(modalData.employee_id, newDate);
                    if (ctx) setModalData((prev) => ({ ...prev, date: newDate,
                      theoretical_hours: ctx.theoretical_hours,
                      worked_hours: ctx.worked_hours,
                      allattamento_max_effettivo: ctx.allattamento_max_effettivo,
                      allattamento_max_normativa: ctx.allattamento_max_normativa,
                    }));
                  }} data-testid="modal-date-input" />
                </div>
                {!modalData.original_date && (
                  <div>
                    <Label className="text-xs text-suite-neutral-600 font-semibold">Al</Label>
                    <Input
                      type="date"
                      min={modalData.date}
                      value={modalData.end_date}
                      onChange={(e) => setModalData({ ...modalData, end_date: e.target.value })}
                      data-testid="modal-end-date-input"
                    />
                  </div>
                )}
              </div>
              {modalData.theoretical_hours !== null && modalData.theoretical_hours > 0 && (
                <div className="text-xs text-suite-neutral-500 mt-1">
                  Profilo teorico del giorno: <b className="text-suite-neutral-700">{fmtHours(modalData.theoretical_hours)}h</b>
                  {modalData.worked_hours > 0 && <> · già lavorate <b className="text-suite-neutral-700">{fmtHours(modalData.worked_hours)}h</b></>}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-suite-neutral-600 font-semibold mb-1 block">Causale</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {(exceptionTypes.length ? exceptionTypes : EXCEPTION_TYPES).map((t) => {
                  const Icon = t.icon;
                  const active = modalData.cause === t.code;
                  return (
                    <button
                      key={t.code}
                      type="button"
                      onClick={() => handleCauseChange(t.code)}
                      data-testid={`modal-cause-${t.code}`}
                      className={`p-2.5 rounded-md border text-xs flex flex-col items-center gap-1 transition-colors ${
                        active
                          ? "border-suite-primary bg-suite-primary-light text-suite-primary font-semibold"
                          : "border-suite-neutral-200 hover:border-suite-neutral-400 text-suite-neutral-600"
                      }`}
                    >
                      <Icon size={15} />
                      <span className="text-center leading-tight">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <DurationLabel className="text-xs text-suite-neutral-600 font-semibold" data-testid="modal-hours-input">Ore</DurationLabel>
              {!modalData.original_date && (
                <div className="space-y-1.5 my-1.5">
                  <label className="flex items-center gap-2 text-xs text-suite-neutral-600">
                    <input
                      type="checkbox"
                      checked={modalData.use_profile_hours}
                      onChange={(e) => setModalData({ ...modalData, use_profile_hours: e.target.checked })}
                      data-testid="modal-use-profile-hours"
                    />
                    Usa automaticamente le ore teoriche di ciascuna giornata lavorativa
                  </label>
                  <div className="text-[11px] text-suite-neutral-500 rounded-md bg-suite-neutral-50 border border-suite-neutral-200 px-2 py-1.5"
                       data-testid="modal-range-day-rule">
                    Regola della causale:{" "}
                    <b className="text-suite-neutral-700">
                      {modalData.range_day_mode === "calendar_days"
                        ? "tutti i giorni consecutivi"
                        : modalData.range_day_mode === "exclude_sundays"
                          ? "tutti i giorni tranne la domenica"
                          : "solo giorni lavorativi"}
                    </b>.
                    {" "}La regola si modifica in Impostazioni → Causali assenza.
                  </div>
                </div>
              )}
              <DurationInput value={modalData.hours}
                     disabled={!modalData.original_date && modalData.use_profile_hours}
                     onCommit={(n) => setModalData((m) => ({ ...m, hours: n ?? "" }))}
                     data-testid="modal-hours-input" />
              {modalData.cause === "ALLATTAMENTO" && modalData.allattamento_max_effettivo !== null && (
                <div className="text-xs text-suite-neutral-600 mt-1 p-2 bg-suite-warning-light/60 border border-suite-warning-border rounded-md" data-testid="allattamento-hint">
                  <b>Allattamento</b>: massimo <b>{fmtHours(modalData.allattamento_max_effettivo)}h</b>
                  {" "}(normativa {modalData.allattamento_max_normativa}h · profilo {fmtHours(modalData.theoretical_hours || 0)}h
                  {" "}– lavorate {fmtHours(modalData.worked_hours || 0)}h).
                  {modalData.allattamento_max_effettivo < modalData.allattamento_max_normativa && (
                    <> Le ore restanti vanno registrate con altra causale.</>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-suite-neutral-600 font-semibold">Note (opzionale)</Label>
              <Textarea value={modalData.notes} onChange={(e) => setModalData({ ...modalData, notes: e.target.value })}
                        placeholder="es. Visita medica, certificato ospedale…" rows={2}
                        data-testid="modal-notes-input" />
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <div>
              {modalData.original_date && (
                <SuiteButton variant="ghost" size="sm" icon={Trash2}
                        onClick={removeExceptionFromModal}
                        className="text-suite-error hover:bg-suite-error-light"
                        data-testid="modal-delete-btn">
                  Elimina
                </SuiteButton>
              )}
            </div>
            <div className="flex gap-2">
              <SuiteButton variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Annulla</SuiteButton>
              <SuiteButton size="sm" onClick={saveException} data-testid="modal-save-btn">
                {modalData.original_date ? "Salva modifiche" : "Registra"}
              </SuiteButton>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
